import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { Search, Plus, X } from "lucide-react";

const CATEGORIES = ["General", "Password / Login", "Email", "Hardware", "Software", "Network / Wi-Fi", "AWS WorkSpaces", "Printer", "Access Request", "Other"];
const EMPTY_T = { requester_id: "", title: "", category: "General", priority: "medium", description: "" };

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_T);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/tickets", { params: { limit: 500 } }).then(r => setItems(asArray(r.data))).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/users").then(r => setUsers(asArray(r.data))).catch(() => setUsers([])); }, []);

  const createTicket = async () => {
    if (!form.title.trim()) { toast.error("A subject is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/tickets", { ...form, requester_id: form.requester_id || undefined });
      toast.success(`Ticket #${data.number} created`);
      setForm(EMPTY_T); setCreating(false);
      nav(`/tickets/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const filtered = items.filter(t => !q || (t.title || "").toLowerCase().includes(q.toLowerCase())).filter(t => !status || t.status === status).filter(t => !priority || t.priority === priority);
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · PSA</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Tickets ({filtered.length})</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search tickets" data-testid="ticket-search" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-72 focus:outline-none focus:border-blue-500/60" />
          </div>
          <select value={status} onChange={e=>setStatus(e.target.value)} data-testid="ticket-status-filter" className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm">
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
          </select>
          <select value={priority} onChange={e=>setPriority(e.target.value)} data-testid="ticket-priority-filter" className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm">
            <option value="">All priority</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <button onClick={() => setCreating(true)} data-testid="ticket-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-1.5 whitespace-nowrap"><Plus size={15} /> New ticket</button>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setCreating(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
              <h2 className="font-display text-lg font-semibold">Log a ticket</h2>
              <button onClick={() => setCreating(false)} className="p-1.5 rounded hover:bg-zinc-900 text-zinc-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Requester (on behalf of)</label>
                <select value={form.requester_id} onChange={e => setForm({ ...form, requester_id: e.target.value })} data-testid="nt-requester" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                  <option value="">— Me ({"logging for myself"}) —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Pick the person who needs help — their name, email and company are attached to the ticket.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Subject *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="nt-title" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="nt-cat" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Priority</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="nt-prio" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm capitalize">
                    {["low", "medium", "high", "critical"].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Details</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="nt-desc" rows={5} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setCreating(false)} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm rounded px-4 py-2">Cancel</button>
                <button onClick={createTicket} disabled={saving} data-testid="nt-save" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">{saving ? "Creating…" : "Create ticket"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-3 w-20">#</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Requester</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">SLA</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(t => (
              <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-zinc-500 text-xs">#{t.number}</td>
                <td className="px-4 py-2"><Link to={`/tickets/${t.id}`} className="hover:text-blue-400" data-testid={`ticket-link-${t.id}`}>{t.title}</Link></td>
                <td className="px-4 py-2">
                  {t.requester_name
                    ? <div><div className="text-zinc-200">{t.requester_name}</div>{t.requester_email && <div className="text-[11px] text-zinc-500 font-mono">{t.requester_email}</div>}</div>
                    : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-2 text-zinc-400">{t.category}</td>
                <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
                <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{t.sla_deadline?.slice(0,10)}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{t.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
